import apiRequest from '../api/request';
import { INTERVAL_LOGO_DATA_URI } from '../img/intervalLogoDataUri';

/**
 * Signs the current user in to InteTasks.
 *
 * Flow: ask our backend for a short-lived signed token, then have the browser
 * POST it to InteTasks, which verifies it, checks the employee against the
 * database, and issues its own session.
 *
 * The token is POSTed rather than put in a query string. It is a full sign-in
 * credential, and query strings end up in reverse-proxy access logs, browser
 * history, and the Referer header sent to whatever the next page loads.
 *
 * Same shape as `dataAccessSso.js` — the two consoles are different apps but
 * the handshake is the same problem, and it is worth them not drifting apart.
 */

const ESCAPE = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapeHtml = (value) => String(value).replace(/[&<>"']/g, (c) => ESCAPE[c]);

/**
 * The page shown in the new tab while the handshake completes.
 *
 * It is on screen for well under a second on a good connection, but it is the
 * seam between two systems — an unbranded page there reads as an interception.
 * The mark makes the handoff obviously ours.
 */
function interstitial(action, token) {
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><title>Signing you in…</title>
<meta name="robots" content="noindex">
<style>
  :root {
    --ground:#FFFFFF; --ink:#4A5262; --faint:#9AA2B2; --track:#EAEDF2;
    --purple:#7B4B8E; --blue-deep:#1D6FB8; --blue:#3399CF;
  }
  @media (prefers-color-scheme: dark) {
    :root { --ground:#0F1115; --ink:#A7AEBC; --faint:#6B7280; --track:#20242C }
  }
  * { box-sizing:border-box }
  body {
    margin:0; height:100vh; background:var(--ground); color:var(--ink);
    font:14px/1.6 system-ui,-apple-system,"Segoe UI",sans-serif;
    display:flex; flex-direction:column; align-items:center; justify-content:center; gap:26px;
  }
  .logo { width:min(260px, 62vw); height:auto; display:block }
  /* Fades in rather than appearing hard, so a fast handshake does not flash. */
  .stage { display:flex; flex-direction:column; align-items:center; gap:22px;
           animation:rise .45s ease-out both }
  @keyframes rise { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:none } }

  .track { width:min(260px,62vw); height:3px; border-radius:2px;
           background:var(--track); overflow:hidden; position:relative }
  .track::after {
    content:''; position:absolute; inset:0; width:45%; border-radius:2px;
    background:linear-gradient(90deg,var(--purple),var(--blue-deep),var(--blue));
    animation:sweep 1.15s cubic-bezier(.65,0,.35,1) infinite;
  }
  @keyframes sweep {
    0%   { transform:translateX(-100%) }
    100% { transform:translateX(322%) }
  }
  p { margin:0; font-size:12.5px; color:var(--faint); letter-spacing:.01em }

  @media (prefers-reduced-motion: reduce) {
    .stage { animation:none }
    .track::after { animation:none; width:100% }
  }
</style></head>
<body>
  <div class="stage">
    <img class="logo" src="${INTERVAL_LOGO_DATA_URI}" alt="Interval — Personalised Learning Platform">
    <div class="track" role="progressbar" aria-label="Signing you in"></div>
    <p>Signing you in to InteTasks…</p>
  </div>

  <form id="sso" method="POST" action="${escapeHtml(action)}">
    <input type="hidden" name="token" value="${escapeHtml(token)}">
    <noscript><button type="submit">Continue</button></noscript>
  </form>
  <script>document.getElementById('sso').submit();<\/script>
</body></html>`;
}

/**
 * @param {(message: string) => void} onError called with a message a user can act on
 */
export async function openInteTasks(onError) {
  // Opened synchronously, before any await.
  //
  // Browsers only allow window.open while a user gesture is still in scope.
  // Requesting the token first and opening afterwards loses that scope and the
  // tab gets blocked, so the tab is opened empty up front and its contents are
  // written once the token arrives.
  const tab = window.open('', '_blank');

  if (!tab) {
    onError('Your browser blocked the new tab. Allow pop-ups for this site and try again.');
    return;
  }

  try {
    const res = await apiRequest({ method: 'post', url: 'intetasks/sso-token' });

    if (res?.status !== 'success' || !res?.data?.token) {
      tab.close();
      onError(res?.message || 'Could not open InteTasks.');
      return;
    }

    tab.document.open();
    tab.document.write(interstitial(res.data.url, res.data.token));
    tab.document.close();
  } catch (error) {
    tab.close();
    onError('Could not reach InteTasks. Please try again.');
  }
}
